import { TaskList, TaskListGen } from "@/types";
import { getData, postData, putData, deleteData } from "./axios-service"
import { TaskListStatus } from "@/types";
import { Stop, TaskListDirection } from "@/types/task-list-direction";
import { InProgressTaskList } from "@/types/in-progress-task-list";


function taskListMapper(inputTaskList: any): TaskList {
    const codeTournee = inputTaskList.codeTournee || "";
    const status = inputTaskList.taskListStatus || "";
    return {
        id: inputTaskList.id,
        codeTournee,
        startDate: inputTaskList.startDate,
        endDate: inputTaskList.endDate,
        taskListStatus: inputTaskList.taskListStatus,
        technicianIds: inputTaskList.technicianIds,
        technicianNames: inputTaskList.technicianNames,
        label: `${codeTournee} - ${status}`.trim(),
        startAddressLine1: inputTaskList.startAddressLine1,
        startAddressLine2: inputTaskList.startAddressLine2,
        startAddressCity: inputTaskList.startAddressCity,
        startAddressPostalCode: inputTaskList.startAddressPostalCode,
        endAddressLine1: inputTaskList.endAddressLine1,
        endAddressLine2: inputTaskList.endAddressLine2,
        endAddressCity: inputTaskList.endAddressCity,
        endAddressPostalCode: inputTaskList.endAddressPostalCode,
        vehicleId: inputTaskList.vehicleId,
        startGeoAddressId: inputTaskList.startGeoAddressId,
        endGeoAddressId: inputTaskList.endGeoAddressId
    };
}

function taskListMapperArray(inputArray: any[]): TaskList[] {
    return inputArray.map(taskListMapper);
}


function inProgressTaskListMapper(inputInProgressTaskList: any): InProgressTaskList {
    return {
        codeTournee: inputInProgressTaskList.codeTournee,
        technicians: inputInProgressTaskList.technicians,
        steps: inputInProgressTaskList.steps.map((step: any) => ({
            rank: step.rank,
            title: step.title,
            status: step.status
        }))
    };
}

function inProgressTaskListMapperArray(inputArray: any[]): InProgressTaskList[] {
    return inputArray.map(inProgressTaskListMapper);
}

function stopMapper(inputStop: any): Stop {
    return {
        from: inputStop.from,
        to: inputStop.to,
        distance: inputStop.distance,
        duration: inputStop.duration
    };
}

function taskListDirectionMapper(inputTaskListDirection: any): TaskListDirection {
    const stops = inputTaskListDirection.stops ?? inputTaskListDirection.stops ?? [];
    return {
        totalDistance: inputTaskListDirection.totalDistance,
        totalDuration: inputTaskListDirection.totalDuration,
        stops: stops.map(stopMapper)
    };
}


export async function getFilteredTaskLists(statuses?: TaskListStatus[], userId?: string, givenDate?: Date): Promise<TaskList[]> {
    const params = new URLSearchParams();

    if (statuses && statuses.length > 0) {
        params.append("status", statuses.join(","));
    }

    if (userId) {
        params.append("user_id", userId);
    }

    if (givenDate) {
        params.append("given_date", givenDate.toISOString().split('T')[0]);
    }

    const query = params.toString();
    const url = query ? `/tasklists?${query}` : "/tasklists";

    const response = await getData<TaskList[]>(url);
    return taskListMapperArray(response ?? []);
}

// Return the maintenance history for a laverie
export async function getTaskListsHistory(laverieId: string): Promise<TaskList[]> {
    const response = await getData<TaskList[]>("/tasklists/history/" + laverieId);
    return taskListMapperArray(response ?? []);
}

export function getTaskListGenById(id: string): Promise<TaskListGen> {
    return getData<TaskListGen>("/tasklists/" + id + "/gen").then(
        onSucceed => {
            return onSucceed;
        }
    );
}

export function getTaskListById(id: string): Promise<TaskList> {
    return getData("/tasklists/" + id).then(
        onSucceed => {
            return taskListMapper(onSucceed);
        }
    );
}

export function postTaskList(taskList: TaskList): Promise<TaskList> {
    return postData("/tasklists", taskList).then(
        onSucceed => {
            return taskListMapper(onSucceed);
        }
    );
}

export function assignTasksToList(
    taskListId: string,
    taskIds: string[]
): Promise<void> {
    return postData(`/tasklists/${taskListId}/assign-tasks`, { taskIds }).then(
        () => {
            return
        }
    )
}

export function assignVehicleToTaskList(taskListId: string, vehicleId: string): Promise<void> {
    return putData(`/tasklist-vehicles/${taskListId}/assign-vehicle`, { vehicleId }).then(
        () => {
            return
        }
    )
}

export function putTaskList(taskList: TaskList): Promise<any> {
    return putData("/tasklists/" + taskList.id, taskList);
}

export function deleteTaskList(ids: string[]): Promise<any> {
    const idsStr = ids.join(',');
    return deleteData("/tasklists/" + idsStr);
}

export async function searchForTaskLists(searchQuery: string): Promise<TaskList[]> {
    const response = await getData<TaskList[]>("/tasklists?" + searchQuery);
    return taskListMapperArray(response ?? []);
}

export async function postOptimizeRoute(taskListId: string): Promise<TaskList> {
    const response = await postData<TaskList>("/tasklists/" + taskListId + "/optimize-route");
    return response;
}

export async function fetchTaskListDirection(taskListId: string): Promise<TaskListDirection> {
    const response = await getData<TaskListDirection>("/tasklists/" + taskListId + "/direction");
    return taskListDirectionMapper(response);
}

export async function fetchInProgressTaskLists(): Promise<InProgressTaskList[]> {
    const response = await getData<InProgressTaskList[]>("/tasklists/in-progress-and-done-today");
    return inProgressTaskListMapperArray(response ?? []);
}